import { z } from 'zod';
import { EntityIdSchema } from './common';

/**
 * Request de POST /api/v1/auth/login (login web).
 * Body: `{ email, password }`.
 *
 * El campo `email` acepta tanto emails válidos ("user@uptm.local") como
 * usernames o cédulas de operador ("faugust", "V-12345678"). El backend
 * intenta login de usuario primero, luego operador (por email o cédula).
 */
export const LoginRequestSchema = z.object({
  email: z.string().min(1),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Actor resumido devuelto en login OK y /auth/me.
 *
 * Para operador, `email` puede ser null (la cédula es el identificador de fallback).
 */
export const ActorSchema = z.object({
  id: EntityIdSchema,
  email: z.string(), // null para operador sin email; usar cédula como fallback
  nombre: z.string(),
  apellido: z.string(),
});
export type Actor = z.infer<typeof ActorSchema>;

/**
 * Response 200 OK sin 2FA.
 * Body: `{ status: 'ok', actor_tipo, actor, permisos, expires_in }`.
 */
export const LoginOkResponseSchema = z.object({
  status: z.literal('ok'),
  actor_tipo: z.union([z.literal('usuario'), z.literal('operador')]),
  actor: ActorSchema,
  permisos: z.array(z.string()),
  expires_in: z.number().int().positive(),
});
export type LoginOkResponse = z.infer<typeof LoginOkResponseSchema>;

/**
 * Response 200 OK con 2FA requerido.
 * Body: `{ status: 'totp_required', temp_token, expires_in }`.
 */
export const LoginTotpRequiredResponseSchema = z.object({
  status: z.literal('totp_required'),
  temp_token: z.string().min(1),
  expires_in: z.number().int().positive(),
});
export type LoginTotpRequiredResponse = z.infer<typeof LoginTotpRequiredResponseSchema>;

/**
 * Response de POST /auth/login — union desnormalizada por `status`.
 */
export const LoginResponseSchema = z.discriminatedUnion('status', [
  LoginOkResponseSchema,
  LoginTotpRequiredResponseSchema,
]);
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * Request de POST /api/v1/auth/login/totp.
 * Body: `{ temp_token, code }`.
 */
export const LoginTotpRequestSchema = z.object({
  temp_token: z.string().min(1),
  code: z.string().regex(/^\d{6}$/),
});
export type LoginTotpRequest = z.infer<typeof LoginTotpRequestSchema>;

/**
 * Response de GET /api/v1/auth/me.
 * NOTA: el backend actual NO incluye `apellido` en esta respuesta (gap menor).
 */
export const MeResponseSchema = z.object({
  id: EntityIdSchema,
  email: z.string().email(),
  nombre: z.string(),
  grupos: z.array(z.string()),
  permisos: z.array(z.string()),
  actor_tipo: z.string(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/**
 * Request de POST /api/v1/auth/activate.
 * Body: `{ token, password }`.
 */
export const ActivateRequestSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type ActivateRequest = z.infer<typeof ActivateRequestSchema>;

/**
 * Request de POST /api/v1/auth/forgot-password.
 * Body: `{ email }`.
 */
export const ForgotPasswordRequestSchema = z.object({
  email: z.string().email(),
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

/**
 * Request de POST /api/v1/auth/reset-password.
 * Body: `{ token, password }`.
 */
export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

/**
 * Request de POST /api/v1/auth/forgot-qr (público).
 * Body: `{ cedula }`.
 */
export const ForgotQrRequestSchema = z.object({
  cedula: z.string().min(1),
});
export type ForgotQrRequest = z.infer<typeof ForgotQrRequestSchema>;